import classNames from "classnames";
import i18next from "i18next";
import { html } from "lit-html";
import { MenuItemId, MenuItemWithDropdownContent } from "./header";
import { useCultureSelector } from "./useCultureSelector";
import { useState } from "Shared/haunted/CustomHooks";
import { useReduxState } from "Shared/redux/useReduxState";
import { TestIdDictionary } from "Services/test-ids/TestIdDictionary";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import { useLogger } from "Services/logging/logger/useLogger";

export interface Props {
	absoluteAction: string;
	activeMenuItemId: MenuItemId;
	bookingUrl: string;
	culture: string;
	dropdownMenus: MenuItemWithDropdownContent[];
	dynamicSettings: DynamicSettings;
	isCollapsed: boolean;
	closeLoginWidget: () => void;
	onRegisterAccountClick: () => void;
	toggleMenuItem: (itemId: MenuItemId) => void;
}

export const useMobileMenuDropdown = (props: Props) => {
	const { logEvent } = useLogger();

	const [userInfo] = useReduxState("userInfo");

	const [isOpen, setIsOpen] = useState<boolean>(false);

	const cultureSelector = useCultureSelector({
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		isHeaderCollapsed: props.isCollapsed,
		onOpen: props.closeLoginWidget,
	});

	const handleLogout = () => {
		logEvent("user_logout", {});
		const url = `${props.bookingUrl}/Member/Logout?culture=${props.culture}&url=${props.absoluteAction}`;
		window.setTimeout(() => (window.location.href = url), 500);
	};

	const logoutTemplate = () =>
		!userInfo?.AmericanAirlines?.IsMember && userInfo?.Name
			? html`
					<li>
						<a class="text-brand-secondary underline md:hover:text-brand-primary md:hover:no-underline" href="#" @click=${handleLogout} data-test-id=${TestIdDictionary.Menu.LogoutOpener}>
							<span>${i18next.t("layout-logout")}</span>
						</a>
					</li>
			  `
			: "";

	const logoImageTemplate = () => html`
		<img class="max-h-[30px] common-transition" src="/images/header/jetsmart-logo-colored.svg" alt="JetSMART" />
	`;

	const closerTemplate = () => html`
		<div
			class="cursor-pointer select-none p-2 text-3xl font-thin"
			aria-label="Close menu"
			@click=${() => setIsOpen(false)}
		>
			&times;
		</div>
	`;

	const menuItemTemplate = (item: MenuItemWithDropdownContent) =>
		html`
			<dc-accordion
				class="w-full border-x-0 border-b border-t-0 border-solid border-b-[#E0E0E0] py-4"
				.contentTemplate=${item.mobileContent}
				.headerClass=${classNames({ "text-n-cyan font-bold": props.activeMenuItemId === item.id })}
				.headerIconClass=${classNames({
					"border-t-n-cyan": props.activeMenuItemId === item.id,
					"border-t-[#204071]": props.activeMenuItemId !== item.id,
				})}
				.headerTitle=${item.title}
				.hideBorder=${true}
				.isOpen=${props.activeMenuItemId === item.id}
				.onClick=${() => props.toggleMenuItem(item.id)}
			></dc-accordion>
		`;

	const registerOrLogoutTemplate = () =>
		!userInfo?.Name
			? html`
					<div
						class="min-w-[345px] max-w-[500px] grow rounded-full border border-solid border-n-blue px-2 py-2 text-center"
						@click=${props.onRegisterAccountClick}
					>
						${i18next.t("Inscríbete aquí")}
					</div>
			  `
			: logoutTemplate();

	const htmlTemplate = () => html`
		<div
			class=${classNames(
				"fixed text-n-blue flex flex-col gap-4 top-0 right-0 h-full w-full bg-white z-[111111] transform transition-transform duration-300 ease-in-out overflow-y-auto items-center md:hidden",
				{ "translate-x-0": isOpen, "translate-x-full": !isOpen }
			)}
		>
			<div class="flex w-full items-center justify-between px-4 pt-2">
				${logoImageTemplate()} ${closerTemplate()}
			</div>
			<ul class="flex w-full list-none flex-col p-4">
				${props.dropdownMenus.map(menuItemTemplate)}
				<li class="flex w-full cursor-pointer justify-center border-b border-solid border-[#E0E0E0] py-4">
					${registerOrLogoutTemplate()}
				</li>
				<li class="w-full border-b border-solid border-[#E0E0E0]">${cultureSelector.htmlTemplate()}</li>
			</ul>
		</div>
	`;

	return {
		closeMobileMenu: () => setIsOpen(false),
		htmlTemplate,
		toggleMobileMenu: () => setIsOpen(!isOpen),
	};
};
